/**
 * ProjectionCard Component
 *
 * Displays a single financial projection metric in a card format.
 * Used in the Projections Summary dashboard to show company-wide aggregated metrics.
 *
 * @module components/budgets/ProjectionCard
 */

import React from 'react'
import { formatCurrency } from '@/types/budgets'
import type { LucideIcon } from 'lucide-react'

export interface ProjectionCardProps {
  /** Card title (e.g., "Total Revenue", "Total Costs") */
  title: string
  /** Monetary value to display */
  value: number | null
  /** Optional icon component from lucide-react */
  icon?: LucideIcon
  /** Optional className for custom styling */
  className?: string
}

/**
 * ProjectionCard - Displays financial projection metric
 *
 * @example
 * import { DollarSign } from 'lucide-react'
 *
 * <ProjectionCard
 *   title="Total Revenue"
 *   value={45000000}
 *   icon={DollarSign}
 * />
 */
export function ProjectionCard({ title, value, icon: Icon, className = '' }: ProjectionCardProps) {
  return (
    <div
      className={`
        rounded-monumenta
        bg-brand-dark-light
        border border-brand-dark-lighter
        p-6
        shadow-sm
        hover:shadow-md
        transition-shadow
        duration-200
        ${className}
      `}
      data-testid="projection-card"
    >
      {/* Header with Icon and Title */}
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-sm font-medium text-brand-gray font-body uppercase tracking-wide">
          {title}
        </h3>
        {Icon && (
          <div className="flex-shrink-0">
            <Icon className="w-5 h-5 text-brand-primary" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Value Display */}
      <div className="mt-2">
        <p
          className="text-3xl font-bold text-white font-display bg-gradient-to-r from-white to-brand-primary bg-clip-text text-transparent"
          data-testid="projection-value"
        >
          {formatCurrency(value)}
        </p>
      </div>
    </div>
  )
}

/**
 * ProjectionCardSkeleton - Loading skeleton for ProjectionCard
 *
 * @example
 * {loading && <ProjectionCardSkeleton />}
 */
export function ProjectionCardSkeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`
        rounded-monumenta
        bg-brand-dark-light
        border border-brand-dark-lighter
        p-6
        shadow-sm
        animate-pulse
        ${className}
      `}
      data-testid="projection-card-skeleton"
      role="status"
      aria-label="Loading projection card"
    >
      {/* Header Skeleton */}
      <div className="flex items-start justify-between mb-4">
        <div className="h-4 bg-brand-dark-lighter rounded w-24"></div>
        <div className="h-5 w-5 bg-brand-dark-lighter rounded"></div>
      </div>

      {/* Value Skeleton */}
      <div className="mt-2">
        <div className="h-10 bg-brand-dark-lighter rounded w-32"></div>
      </div>
    </div>
  )
}
